import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';

/** Corps pour POST /api/parametrage/coordinate-params */
export interface CoordinateParamsPayload {
  station_id: number;
  user_id: number;
  param_table_id: number;
  valeur: string;
  /** Produit (écran produit). */
  product_id?: number;
  /** Client (écran client). */
  client_id?: number;
  /** Fournisseur (écran fournisseur). */
  fournisseur_id?: number;
}

/**
 * Extrait la valeur d’une réponse GET coordinate-params (formes API variables).
 * @param idField clé d’identifiant entité dans les objets liste (`product_id`, `client_id`, `fournisseur_id`, …).
 */
export function parseCoordinateValeurFromResponse(
  raw: unknown,
  entityId: number,
  idField: string
): string {
  if (raw == null) {
    return '';
  }
  const anyRaw = raw as Record<string, unknown>;
  const direct = anyRaw['valeur'] ?? anyRaw['value'];
  if (typeof direct === 'string' || typeof direct === 'number') {
    return String(direct);
  }
  const data = anyRaw['data'];
  const list: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(anyRaw['rows'])
      ? (anyRaw['rows'] as unknown[])
      : Array.isArray(raw)
        ? (raw as unknown[])
        : [];
  if (list.length > 0) {
    const forEntity = list.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        Number((item as Record<string, unknown>)[idField]) === entityId
    ) as Record<string, unknown> | undefined;
    const pick = forEntity ?? (list[0] as Record<string, unknown>);
    const v = pick?.['valeur'] ?? pick?.['value'];
    if (v != null) {
      return String(v);
    }
  }
  return '';
}

/** Corps d'une ligne pour POST /api/parametrage/param-tables */
export interface ParamTableRowPayload {
  station_id: number;
  user_id: number;
  table_db: string;
  champ_db: string;
  libelle_champ: string;
  type: string;
}

@Injectable({
  providedIn: 'root'
})
export class ParamTablesService {
  private readonly url = `${String(environment.apiUrl).replace(/\/$/, '')}/api/parametrage/param-tables`;

  constructor(private http: HttpClient) {}

  postParamTableRow(body: ParamTableRowPayload): Observable<unknown> {
    return this.http.post<unknown>(this.url, body);
  }

  /** Une requête par ligne (champ_db param1…paramN). */
  postParamTableRows(rows: ParamTableRowPayload[]): Observable<unknown[]> {
    if (rows.length === 0) {
      return of([]);
    }
    return forkJoin(rows.map((r) => this.postParamTableRow(r)));
  }

  /**
   * GET /api/parametrage/param-tables/by-table/:tableDb?station_id=&user_id=
   * Ex. products → /by-table/products?station_id=&user_id=
   */
  getParamTablesByTable(
    tableDb: string | number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.url}/by-table/${encodeURIComponent(String(tableDb))}`;
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId));
    return this.http.get<unknown>(path, { params });
  }

  private readonly coordinateUrl = `${String(environment.apiUrl).replace(/\/$/, '')}/api/parametrage/coordinate-params`;

  /** Même hôte que les autres routes `select-params` (souvent `parametrage_url`). */
  private readonly selectParamsUrl = `${String(
    environment.parametrage_url || environment.apiUrl
  ).replace(/\/$/, '')}/api/parametrage/select-params`;

  /**
   * GET /api/parametrage/select-params/param-table/:paramTableId?station_id=&user_id=
   * Options affichables pour un champ paramètre de type liste (select).
   */
  getSelectParamsByParamTable(
    paramTableId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.selectParamsUrl}/param-table/${encodeURIComponent(String(paramTableId))}`;
    const params = new HttpParams()
      .set('station_id', String(Math.floor(Number(stationId))))
      .set('user_id', String(Math.floor(Number(userId))));
    return this.http.get<unknown>(path, { params });
  }

  /** POST /api/parametrage/coordinate-params */
  postCoordinateParam(body: CoordinateParamsPayload): Observable<unknown> {
    return this.http.post<unknown>(this.coordinateUrl, body);
  }

  /** Une requête par ligne (param_table_id + valeur pour un produit). */
  postCoordinateParams(rows: CoordinateParamsPayload[]): Observable<unknown[]> {
    if (rows.length === 0) {
      return of([]);
    }
    return forkJoin(rows.map((r) => this.postCoordinateParam(r)));
  }

  /**
   * GET /api/parametrage/coordinate-params/by-param-table/:paramTableId/product/:productId?station_id=&user_id=
   * `param_table_id` = id renvoyé par GET param-tables/by-table/products.
   */
  getCoordinateParamsByParamTableProduct(
    paramTableId: number,
    productId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.coordinateUrl}/by-param-table/${encodeURIComponent(String(paramTableId))}/product/${encodeURIComponent(String(productId))}`;
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId));
    return this.http.get<unknown>(path, { params });
  }

  /**
   * GET …/by-param-table/:paramTableId/client/:clientId?station_id=&user_id=
   */
  getCoordinateParamsByParamTableClient(
    paramTableId: number,
    clientId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.coordinateUrl}/by-param-table/${encodeURIComponent(String(paramTableId))}/client/${encodeURIComponent(String(clientId))}`;
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId));
    return this.http.get<unknown>(path, { params });
  }

  /**
   * GET …/by-param-table/:paramTableId/fournisseur/:fournisseurId?station_id=&user_id=
   */
  getCoordinateParamsByParamTableFournisseur(
    paramTableId: number,
    fournisseurId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.coordinateUrl}/by-param-table/${encodeURIComponent(String(paramTableId))}/fournisseur/${encodeURIComponent(String(fournisseurId))}`;
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId));
    return this.http.get<unknown>(path, { params });
  }
}
